/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { MarketSegment, RegionalMarket } from '@/types/tycoon';
import type { Product } from '@/types/product';

interface CustomerProfile {
  id: string;
  segmentId: string;
  demographics: {
    age: number;
    income: number;
    education: 'high_school' | 'college' | 'graduate';
    occupation: 'student' | 'professional' | 'business' | 'creative' | 'retired';
  };
  preferences: {
    priceRange: [number, number];
    techFeatures: string[];
    brandPreference: string[];
    usagePatterns: 'light' | 'moderate' | 'heavy' | 'professional';
  };
  behaviors: {
    purchaseFrequency: number; // years between purchases
    researchTime: number; // days spent researching
    brandLoyalty: number; // 0-1
    priceElasticity: number; // 0-2+
    influenceFactors: ('reviews' | 'ads' | 'social' | 'recommendations')[];
  };
}

interface SegmentTrends {
  segmentId: string;
  growthTrajectory: 'declining' | 'stable' | 'growing' | 'exploding';
  emergingNeeds: string[];
  satisfactionGaps: {
    feature: string;
    gapSize: number; // 0-1
    importance: number; // 0-1
  }[];
  competitiveIntensity: number; // 0-1
  priceEvolution: 'deflating' | 'stable' | 'inflating';
}

export class MarketSegmentationService {
  private static instance: MarketSegmentationService;
  private segments: Map<string, MarketSegment> = new Map();
  private customerProfiles: Map<string, CustomerProfile[]> = new Map();
  private segmentTrends: Map<string, SegmentTrends> = new Map();
  private crossSegmentMigration: Map<string, Map<string, number>> = new Map();

  private constructor() {
    this.initializeAdvancedSegments();
    this.initializeCustomerProfiles();
    this.initializeSegmentTrends();
  }

  public static getInstance(): MarketSegmentationService {
    if (!MarketSegmentationService.instance) {
      MarketSegmentationService.instance = new MarketSegmentationService();
    }
    return MarketSegmentationService.instance;
  }

  // Phase 2: Advanced Market Segmentation
  public calculateSegmentFit(product: Product, segmentId: string): number {
    const segment = this.segments.get(segmentId);
    const profiles = this.customerProfiles.get(segmentId);
    
    if (!segment || !profiles) return 0;

    let totalFit = 0;
    profiles.forEach(profile => {
      const fit = this.calculateProductProfileFit(product, profile);
      totalFit += fit;
    });

    return totalFit / profiles.length;
  }

  public identifyOptimalSegments(product: Product): Array<{segmentId: string, fit: number, potential: number}> {
    const segmentAnalysis: Array<{segmentId: string, fit: number, potential: number}> = [];

    this.segments.forEach((segment, segmentId) => {
      const fit = this.calculateSegmentFit(product, segmentId);
      const potential = this.calculateSegmentPotential(segmentId, product);
      
      segmentAnalysis.push({ segmentId, fit, potential });
    });

    return segmentAnalysis
      .sort((a, b) => (b.fit * b.potential) - (a.fit * a.potential))
      .slice(0, 5); // Top 5 segments
  }

  public updateSegmentDynamics(marketData: {
    economicFactors: Record<string, unknown>;
    technologyAdoptions: Record<string, unknown>;
    competitorActions: unknown[];
    timeElapsed: number;
  }): void {
    this.segments.forEach((segment, segmentId) => {
      // Update segment size based on economic and technology factors
      this.updateSegmentSize(segmentId, marketData);
      
      // Update demographic characteristics
      this.updateSegmentDemographics(segmentId, marketData);
      
      // Update preferences and behaviors
      this.updateSegmentPreferences(segmentId, marketData);
      
      // Calculate cross-segment migration
      this.calculateSegmentMigration(segmentId, marketData);
    });

    // Update trends
    this.updateSegmentTrends(marketData);
  }

  public calculateCrossPlatformEffects(product: Product): Map<string, number> {
    const effects = new Map<string, number>();
    
    this.segments.forEach((segment, segmentId) => {
      const directFit = this.calculateSegmentFit(product, segmentId);
      
      // Calculate spillover effects to adjacent segments
      this.crossSegmentMigration.forEach((migrations, fromSegmentId) => {
        const migrationRate = migrations.get(segmentId) || 0;
        const spilloverEffect = directFit * migrationRate * 0.3; // 30% spillover
        
        const currentEffect = effects.get(fromSegmentId) || 0;
        effects.set(fromSegmentId, currentEffect + spilloverEffect);
      });
    });

    return effects;
  }

  public predictSegmentEvolution(timeHorizon: number): Map<string, MarketSegment> {
    const futureSegments = new Map<string, MarketSegment>();

    this.segments.forEach((segment, segmentId) => {
      const trends = this.segmentTrends.get(segmentId);
      if (!trends) return;

      const futureSegment: MarketSegment = {
        ...segment,
        size: this.projectSegmentSize(segment, trends, timeHorizon),
        growthRate: this.adjustGrowthRate(segment.growthRate, trends),
        priceElasticity: this.evolveElasticity(segment.priceElasticity, trends),
        techSensitivity: this.evolveTechSensitivity(segment.techSensitivity, trends),
        brandLoyalty: this.evolveBrandLoyalty(segment.brandLoyalty, trends)
      };

      futureSegments.set(segmentId, futureSegment);
    });

    return futureSegments;
  }

  // Micro-segmentation for targeted marketing
  public identifyMicroSegments(segmentId: string): CustomerProfile[] {
    const profiles = this.customerProfiles.get(segmentId) || [];
    
    // Cluster similar profiles into micro-segments
    const clusters = this.clusterProfiles(profiles);
    
    // Return representative profiles for each cluster
    return clusters.map(cluster => this.createRepresentativeProfile(cluster));
  }

  public calculateSegmentCanibalization(
    newProduct: Product, 
    existingProducts: Product[]
  ): Map<string, number> {
    const canibalization = new Map<string, number>();

    this.segments.forEach((segment, segmentId) => {
      const newProductFit = this.calculateSegmentFit(newProduct, segmentId);
      
      let totalExistingFit = 0;
      existingProducts.forEach(product => {
        totalExistingFit += this.calculateSegmentFit(product, segmentId);
      });

      if (totalExistingFit > 0) {
        const canibalizationRate = newProductFit / (newProductFit + totalExistingFit);
        canibalization.set(segmentId, canibalizationRate);
      }
    });

    return canibalization;
  }

  // Private implementation methods
  private calculateProductProfileFit(product: Product, profile: CustomerProfile): number {
    let fit = 0;

    // Price fit
    const [minPrice, maxPrice] = profile.preferences.priceRange;
    if (product.price >= minPrice && product.price <= maxPrice) {
      fit += 0.3;
    } else {
      const pricePenalty = Math.min(
        Math.abs(product.price - minPrice) / minPrice,
        Math.abs(product.price - maxPrice) / maxPrice
      );
      fit += Math.max(0, 0.3 - pricePenalty);
    }

    // Technology feature fit
    const productFeatures = this.extractProductFeatures(product);
    const featureOverlap = this.calculateFeatureOverlap(
      productFeatures, 
      profile.preferences.techFeatures
    );
    fit += featureOverlap * 0.4;

    // Usage pattern fit
    const usageFit = this.calculateUsageFit(product, profile.preferences.usagePatterns);
    fit += usageFit * 0.3;

    return Math.min(fit, 1.0);
  }

  private calculateSegmentPotential(segmentId: string, product: Product): number {
    const segment = this.segments.get(segmentId);
    const trends = this.segmentTrends.get(segmentId);
    
    if (!segment || !trends) return 0;

    let potential = segment.size / 1000000; // Normalize by millions

    // Growth trajectory impact
    const growthMultipliers = {
      declining: 0.5,
      stable: 1.0,
      growing: 1.5,
      exploding: 2.5
    };
    potential *= growthMultipliers[trends.growthTrajectory];

    // Competition intensity impact
    potential *= (1 - trends.competitiveIntensity * 0.5);

    // Satisfaction gap opportunity
    const avgGapSize = trends.satisfactionGaps.reduce((sum, gap) => 
      sum + gap.gapSize * gap.importance, 0) / trends.satisfactionGaps.length;
    potential *= (1 + avgGapSize);

    return Math.min(potential, 2.0);
  }

  private updateSegmentSize(segmentId: string, marketData: any): void {
    const segment = this.segments.get(segmentId);
    const trends = this.segmentTrends.get(segmentId);
    
    if (!segment || !trends) return;

    let sizeMultiplier = 1.0;

    // Economic factor impact
    if (marketData.economicFactors.economic < 0.8) {
      // Economic downturn shrinks premium segments more
      if (segmentId === 'premium' || segmentId === 'enterprise') {
        sizeMultiplier *= 0.9;
      } else {
        sizeMultiplier *= 1.05; // Budget segments may grow
      }
    }

    // Technology adoption impact
    const techImpact = this.calculateTechnologyImpactOnSegment(segmentId, marketData.technologyAdoptions);
    sizeMultiplier *= techImpact;

    // Apply growth trajectory
    const timeInYears = marketData.timeElapsed / (365 * 24 * 60 * 60 * 1000);
    const growthRates = {
      declining: -0.05,
      stable: 0.02,
      growing: 0.08,
      exploding: 0.20
    };
    const annualGrowth = growthRates[trends.growthTrajectory];
    sizeMultiplier *= Math.pow(1 + annualGrowth, timeInYears);

    segment.size *= sizeMultiplier;
  }

  private updateSegmentDemographics(segmentId: string, marketData: any): void {
    const segment = this.segments.get(segmentId);
    if (!segment) return;

    // Age range evolution (populations aging, new generations entering)
    const [minAge, maxAge] = segment.demographics.ageRange;
    const timeInYears = marketData.timeElapsed / (365 * 24 * 60 * 60 * 1000);
    
    // Gradual aging of segments
    segment.demographics.ageRange = [
      Math.max(16, minAge + timeInYears * 0.1),
      Math.min(85, maxAge + timeInYears * 0.1)
    ];

    // Income evolution based on economic factors
    const incomeMultiplier = marketData.economicFactors.economic;
    const [minIncome, maxIncome] = segment.demographics.incomeRange;
    segment.demographics.incomeRange = [
      minIncome * incomeMultiplier,
      maxIncome * incomeMultiplier
    ];
  }

  private updateSegmentPreferences(segmentId: string, marketData: any): void {
    const segment = this.segments.get(segmentId);
    const profiles = this.customerProfiles.get(segmentId);
    
    if (!segment || !profiles) return;

    // Technology sensitivity evolution
    let techSensitivityDelta = 0;
    
    // Younger segments become more tech-sensitive over time
    const avgAge = (segment.demographics.ageRange[0] + segment.demographics.ageRange[1]) / 2;
    if (avgAge < 35) {
      techSensitivityDelta += 0.01;
    } else if (avgAge > 55) {
      techSensitivityDelta -= 0.005;
    }

    // Technology adoptions influence sensitivity
    const activeTech = Array.from(marketData.technologyAdoptions.values())
      .filter(adoption => adoption.stage === 'early_majority').length;
    techSensitivityDelta += activeTech * 0.005;

    segment.techSensitivity = Math.max(0.1, 
      Math.min(segment.techSensitivity + techSensitivityDelta, 2.0));

    // Price elasticity evolution based on economic conditions
    if (marketData.economicFactors.economic < 0.8) {
      segment.priceElasticity *= 1.1; // More price sensitive in bad times
    } else if (marketData.economicFactors.economic > 1.2) {
      segment.priceElasticity *= 0.95; // Less price sensitive in good times
    }
  }

  private calculateSegmentMigration(segmentId: string, marketData: any): void {
    const segment = this.segments.get(segmentId);
    if (!segment) return;

    const migrations = new Map<string, number>();

    this.segments.forEach((otherSegment, otherSegmentId) => {
      if (segmentId === otherSegmentId) return;

      let migrationRate = 0;

      // Economic migration (premium to budget during downturns)
      if (marketData.economicFactors.economic < 0.8) {
        if (segmentId === 'premium' && otherSegmentId === 'mainstream') {
          migrationRate += 0.05;
        } else if (segmentId === 'mainstream' && otherSegmentId === 'budget') {
          migrationRate += 0.03;
        }
      }

      // Technology migration (early adopters influence mainstream)
      const techGap = Math.abs(segment.techSensitivity - otherSegment.techSensitivity);
      if (techGap < 0.3) {
        migrationRate += 0.02;
      }

      migrations.set(otherSegmentId, migrationRate);
    });

    this.crossSegmentMigration.set(segmentId, migrations);
  }

  private updateSegmentTrends(marketData: any): void {
    this.segmentTrends.forEach((trends, segmentId) => {
      // Update growth trajectory based on recent performance
      const segment = this.segments.get(segmentId);
      if (!segment) return;

      if (segment.growthRate > 0.15) {
        trends.growthTrajectory = 'exploding';
      } else if (segment.growthRate > 0.08) {
        trends.growthTrajectory = 'growing';
      } else if (segment.growthRate > 0.02) {
        trends.growthTrajectory = 'stable';
      } else {
        trends.growthTrajectory = 'declining';
      }

      // Update competitive intensity based on competitor actions
      const competitorActionsInSegment = marketData.competitorActions.filter(action => 
        this.isActionTargetingSegment(action, segmentId));
      trends.competitiveIntensity = Math.min(
        competitorActionsInSegment.length * 0.1, 1.0);

      // Update satisfaction gaps based on market needs
      this.updateSatisfactionGaps(trends, segmentId, marketData);
    });
  }

  // Helper methods
  private extractProductFeatures(product: Product): string[] {
    const features: string[] = [];
    
    // Extract features from design components
    Object.entries(product.designConfig.components).forEach(([componentType, componentId]) => {
      features.push(componentType);
      if (product.techScore > 80) {
        features.push(`advanced_${componentType}`);
      }
    });

    return features;
  }

  private calculateFeatureOverlap(productFeatures: string[], preferredFeatures: string[]): number {
    const overlap = productFeatures.filter(feature => 
      preferredFeatures.includes(feature)).length;
    return overlap / Math.max(preferredFeatures.length, 1);
  }

  private calculateUsageFit(product: Product, usagePattern: string): number {
    const avgTechScore = product.techScore;

    const usageFitMap = {
      light: avgTechScore < 50 ? 1.0 : 0.6,
      moderate: avgTechScore >= 40 && avgTechScore <= 70 ? 1.0 : 0.7,
      heavy: avgTechScore > 60 ? 1.0 : 0.5,
      professional: avgTechScore > 75 ? 1.0 : 0.4
    };

    return usageFitMap[usagePattern] || 0.5;
  }

  private calculateTechnologyImpactOnSegment(segmentId: string, technologyAdoptions: any): number {
    let impact = 1.0;
    
    // Different segments respond differently to technology
    const segmentTechSensitivity = this.segments.get(segmentId)?.techSensitivity || 1.0;
    
    Array.from(technologyAdoptions.values()).forEach((adoption: any) => {
      if (adoption.stage === 'early_majority' || adoption.stage === 'late_majority') {
        impact *= (1 + adoption.marketImpact * segmentTechSensitivity * 0.1);
      }
    });

    return Math.min(impact, 1.5);
  }

  private clusterProfiles(profiles: CustomerProfile[]): CustomerProfile[][] {
    // Simplified clustering - group by similar demographics and preferences
    const clusters: CustomerProfile[][] = [];
    
    // Group by age and income similarity
    const grouped = new Map<string, CustomerProfile[]>();
    
    profiles.forEach(profile => {
      const ageGroup = Math.floor(profile.demographics.age / 10) * 10;
      const incomeGroup = Math.floor(profile.demographics.income / 20000) * 20000;
      const key = `${ageGroup}_${incomeGroup}`;
      
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(profile);
    });

    return Array.from(grouped.values()).filter(cluster => cluster.length > 0);
  }

  private createRepresentativeProfile(cluster: CustomerProfile[]): CustomerProfile {
    // Create average profile from cluster
    const avgAge = cluster.reduce((sum, p) => sum + p.demographics.age, 0) / cluster.length;
    const avgIncome = cluster.reduce((sum, p) => sum + p.demographics.income, 0) / cluster.length;
    
    return {
      id: `micro_segment_${Math.random().toString(36).substr(2, 9)}`,
      segmentId: cluster[0].segmentId,
      demographics: {
        ...cluster[0].demographics,
        age: avgAge,
        income: avgIncome
      },
      preferences: cluster[0].preferences, // Use representative preferences
      behaviors: cluster[0].behaviors
    };
  }

  private projectSegmentSize(segment: MarketSegment, trends: SegmentTrends, timeHorizon: number): number {
    const growthRates = {
      declining: -0.05,
      stable: 0.02,
      growing: 0.08,
      exploding: 0.20
    };
    
    const annualGrowth = growthRates[trends.growthTrajectory];
    return segment.size * Math.pow(1 + annualGrowth, timeHorizon);
  }

  private adjustGrowthRate(currentRate: number, trends: SegmentTrends): number {
    const trendImpact = {
      declining: -0.02,
      stable: 0,
      growing: 0.01,
      exploding: 0.03
    };
    
    return currentRate + trendImpact[trends.growthTrajectory];
  }

  private evolveElasticity(currentElasticity: number, trends: SegmentTrends): number {
    if (trends.priceEvolution === 'deflating') {
      return currentElasticity * 1.1; // More price sensitive
    } else if (trends.priceEvolution === 'inflating') {
      return currentElasticity * 0.9; // Less price sensitive
    }
    return currentElasticity;
  }

  private evolveTechSensitivity(currentSensitivity: number, trends: SegmentTrends): number {
    const techTrend = trends.emergingNeeds.filter(need => 
      need.includes('tech') || need.includes('digital')).length;
    return currentSensitivity * (1 + techTrend * 0.05);
  }

  private evolveBrandLoyalty(currentLoyalty: number, trends: SegmentTrends): number {
    // Higher competitive intensity reduces brand loyalty
    return currentLoyalty * (1 - trends.competitiveIntensity * 0.1);
  }

  private isActionTargetingSegment(action: any, segmentId: string): boolean {
    // Simplified logic to determine if competitor action targets specific segment
    return Math.random() < 0.3; // 30% chance action targets any given segment
  }

  private updateSatisfactionGaps(trends: SegmentTrends, segmentId: string, marketData: any): void {
    // Update satisfaction gaps based on technology evolution and competitor actions
    trends.satisfactionGaps.forEach(gap => {
      // Technology improvements reduce gaps
      if (gap.feature.includes('tech') || gap.feature.includes('performance')) {
        const techProgress = Array.from(marketData.technologyAdoptions.values())
          .filter((adoption: any) => adoption.stage === 'early_majority').length;
        gap.gapSize = Math.max(0, gap.gapSize - techProgress * 0.02);
      }
      
      // Competitor actions may fill gaps
      const relevantActions = marketData.competitorActions.filter((action: any) => 
        action.action === 'product_launch');
      gap.gapSize = Math.max(0, gap.gapSize - relevantActions.length * 0.01);
    });
  }

  // Initialize methods
  private initializeAdvancedSegments(): void {
    const advancedSegments: MarketSegment[] = [
      {
        id: 'budget_conscious',
        name: 'Budget Conscious',
        size: 120000000,
        priceElasticity: 2.0,
        techSensitivity: 0.5,
        brandLoyalty: 0.3,
        growthRate: 0.06,
        demographics: {
          ageRange: [18, 65],
          incomeRange: [15000, 45000],
          techAdoption: 'late'
        }
      },
      {
        id: 'value_seekers',
        name: 'Value Seekers',
        size: 180000000,
        priceElasticity: 1.5,
        techSensitivity: 0.7,
        brandLoyalty: 0.5,
        growthRate: 0.08,
        demographics: {
          ageRange: [25, 55],
          incomeRange: [35000, 70000],
          techAdoption: 'mainstream'
        }
      },
      {
        id: 'tech_enthusiasts',
        name: 'Technology Enthusiasts',
        size: 50000000,
        priceElasticity: 0.9,
        techSensitivity: 1.8,
        brandLoyalty: 0.6,
        growthRate: 0.15,
        demographics: {
          ageRange: [18, 40],
          incomeRange: [40000, 100000],
          techAdoption: 'early'
        }
      },
      {
        id: 'premium_users',
        name: 'Premium Users',
        size: 60000000,
        priceElasticity: 0.6,
        techSensitivity: 1.3,
        brandLoyalty: 0.8,
        growthRate: 0.12,
        demographics: {
          ageRange: [25, 50],
          incomeRange: [70000, 200000],
          techAdoption: 'early'
        }
      },
      {
        id: 'enterprise_professional',
        name: 'Enterprise Professional',
        size: 40000000,
        priceElasticity: 0.4,
        techSensitivity: 1.1,
        brandLoyalty: 0.9,
        growthRate: 0.05,
        demographics: {
          ageRange: [28, 60],
          incomeRange: [60000, 250000],
          techAdoption: 'mainstream'
        }
      },
      {
        id: 'senior_users',
        name: 'Senior Users',
        size: 30000000,
        priceElasticity: 1.2,
        techSensitivity: 0.4,
        brandLoyalty: 0.7,
        growthRate: 0.10,
        demographics: {
          ageRange: [55, 85],
          incomeRange: [25000, 80000],
          techAdoption: 'late'
        }
      }
    ];

    advancedSegments.forEach(segment => {
      this.segments.set(segment.id, segment);
    });
  }

  private initializeCustomerProfiles(): void {
    // Initialize customer profiles for each segment
    this.segments.forEach((segment, segmentId) => {
      const profiles: CustomerProfile[] = [];
      
      // Create 3-5 representative profiles per segment
      const profileCount = 3 + Math.floor(Math.random() * 3);
      
      for (let i = 0; i < profileCount; i++) {
        profiles.push(this.createRandomProfile(segment));
      }
      
      this.customerProfiles.set(segmentId, profiles);
    });
  }

  private createRandomProfile(segment: MarketSegment): CustomerProfile {
    const [minAge, maxAge] = segment.demographics.ageRange;
    const [minIncome, maxIncome] = segment.demographics.incomeRange;
    
    return {
      id: `profile_${Math.random().toString(36).substr(2, 9)}`,
      segmentId: segment.id,
      demographics: {
        age: minAge + Math.random() * (maxAge - minAge),
        income: minIncome + Math.random() * (maxIncome - minIncome),
        education: ['high_school', 'college', 'graduate'][Math.floor(Math.random() * 3)] as any,
        occupation: ['student', 'professional', 'business', 'creative', 'retired'][Math.floor(Math.random() * 5)] as any
      },
      preferences: {
        priceRange: [
          Math.max(minIncome * 0.01, 200),
          Math.min(minIncome * 0.05, 2000)
        ],
        techFeatures: this.generateTechPreferences(segment.techSensitivity),
        brandPreference: ['apple', 'samsung', 'google', 'oneplus'],
        usagePatterns: ['light', 'moderate', 'heavy', 'professional'][Math.floor(Math.random() * 4)] as any
      },
      behaviors: {
        purchaseFrequency: 2 + Math.random() * 3, // 2-5 years
        researchTime: Math.floor(7 + Math.random() * 21), // 1-4 weeks
        brandLoyalty: segment.brandLoyalty + (Math.random() - 0.5) * 0.2,
        priceElasticity: segment.priceElasticity + (Math.random() - 0.5) * 0.4,
        influenceFactors: ['reviews', 'ads', 'social', 'recommendations'].filter(() => Math.random() < 0.5) as any
      }
    };
  }

  private generateTechPreferences(techSensitivity: number): string[] {
    const allFeatures = [
      'camera', 'battery', 'processor', 'display', 'storage',
      'wireless_charging', 'water_resistance', '5g', 'ai_features',
      'biometric_security', 'fast_charging', 'multi_camera'
    ];
    
    const featureCount = Math.floor(techSensitivity * 8);
    return allFeatures.slice(0, featureCount);
  }

  private initializeSegmentTrends(): void {
    this.segments.forEach((segment, segmentId) => {
      const trends: SegmentTrends = {
        segmentId,
        growthTrajectory: segment.growthRate > 0.1 ? 'growing' : 'stable',
        emergingNeeds: this.generateEmergingNeeds(segmentId),
        satisfactionGaps: this.generateSatisfactionGaps(segmentId),
        competitiveIntensity: 0.5 + Math.random() * 0.3,
        priceEvolution: ['deflating', 'stable', 'inflating'][Math.floor(Math.random() * 3)] as any
      };
      
      this.segmentTrends.set(segmentId, trends);
    });
  }

  private generateEmergingNeeds(segmentId: string): string[] {
    const needsMap: { [key: string]: string[] } = {
      budget_conscious: ['longer_battery', 'durability', 'basic_camera'],
      value_seekers: ['better_camera', 'fast_charging', 'storage'],
      tech_enthusiasts: ['ai_features', 'advanced_camera', 'gaming_performance'],
      premium_users: ['luxury_materials', 'exclusive_features', 'status_symbol'],
      enterprise_professional: ['security', 'productivity', 'reliability'],
      senior_users: ['simplicity', 'accessibility', 'health_monitoring']
    };
    
    return needsMap[segmentId] || ['general_improvement'];
  }

  private generateSatisfactionGaps(segmentId: string): Array<{feature: string, gapSize: number, importance: number}> {
    const gaps = [
      { feature: 'battery_life', gapSize: 0.3, importance: 0.9 },
      { feature: 'camera_quality', gapSize: 0.2, importance: 0.8 },
      { feature: 'performance', gapSize: 0.25, importance: 0.7 },
      { feature: 'price_value', gapSize: 0.4, importance: 0.85 },
      { feature: 'durability', gapSize: 0.35, importance: 0.75 }
    ];
    
    // Randomize gap sizes with some segment-specific adjustments
    return gaps.map(gap => ({
      ...gap,
      gapSize: Math.max(0.1, gap.gapSize + (Math.random() - 0.5) * 0.2)
    }));
  }

  // Public getters
  public getSegments(): Map<string, MarketSegment> {
    return this.segments;
  }

  public getCustomerProfiles(): Map<string, CustomerProfile[]> {
    return this.customerProfiles;
  }

  public getSegmentTrends(): Map<string, SegmentTrends> {
    return this.segmentTrends;
  }

  public getCrossSegmentMigration(): Map<string, Map<string, number>> {
    return this.crossSegmentMigration;
  }
}

export const marketSegmentation = MarketSegmentationService.getInstance();